import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Modal,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Header from "../../components/Header";

/* ------------------------------------------------------------------ */
/*  CONSTANTS                                                         */
/* ------------------------------------------------------------------ */
const MEDIA_BASE = "https://media.swancreate.com/images";
const MEDIA_FOLDER = "figfinance";

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                         */
/* ------------------------------------------------------------------ */
export default function SchoolLocations() {
    const theme = useTheme();
    const mode = theme.palette.mode;

    /* ------------------ adaptive palette helpers ------------------ */
    const palette = {
        background: mode === "dark" ? theme.palette.background.default : theme.palette.background.paper,
        panel: theme.palette.background.paper,
        headerBg: theme.palette.action.hover,
        border: theme.palette.divider,
        primaryBtn: theme.palette.primary.main,
        primaryBtnHover: theme.palette.primary.dark,
        secondaryBtn: theme.palette.secondary.main,
        secondaryBtnHover: theme.palette.secondary.dark,
        successBtn: theme.palette.success.main,
        successBtnHover: theme.palette.success.dark,
        text: theme.palette.text.primary,
        error: theme.palette.error.main,
    };

    const modalStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 400,
        bgcolor: palette.panel,
        border: `2px solid ${palette.border}`,
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
    };

    const textFieldSx = {
        bgcolor: palette.headerBg,
        borderRadius: 1,
        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            "& fieldset": { borderColor: palette.text },
            "&:hover fieldset": { borderColor: palette.text },
            "&.Mui-focused fieldset": { borderColor: palette.text },
        },
        "& .MuiInputBase-input": {
            color: palette.text,
            "&::placeholder": { color: palette.text, opacity: 0.7 },
        },
        "& .MuiInputLabel-root": {
            color: palette.text,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: palette.text,
        },
    };

    const selectSx = {
        ...textFieldSx,
        "& .MuiSvgIcon-root": { color: palette.text },
    };

    /* ------------------------- STATE ------------------------------ */
    const [schools, setSchools] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(true);
    const [editingSchoolId, setEditingSchoolId] = useState(null);
    const [updatedData, setUpdatedData] = useState({});

    const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
    const [newSchool, setNewSchool] = useState({
        SchoolName: "",
        SchoolCode: "",
        Address: "",
        City: "",
        State: "",
        Zip: "",
        Email: "",
        PhoneNumber: "",
        IsActive: true,
    });

    const [districtLogo, setDistrictLogo] = useState("");
    const [districtId, setDistrictId] = useState("");
    const [loadingDistrict, setLoadingDistrict] = useState(true);

    /* ------------------------ EFFECTS ----------------------------- */
    useEffect(() => {
        fetchSchools();
        fetchDistrict();
    }, []);

    /* ---------------------- DATA FETCHING ------------------------- */
    const fetchSchools = async () => {
        try {
            const { data } = await axios.get("https://figazuresql.swancreate.com/api/swa/SchoolMst");
            setSchools(data);
        } catch (error) {
            console.error("Error fetching schools data:", error);
        } finally {
            setLoadingSchools(false);
        }
    };

    const fetchDistrict = async () => {
        try {
            const { data } = await axios.get("https://figazuresql.swancreate.com/api/swa/SchoolDistrict");
            if (Array.isArray(data) && data.length > 0) {
                const d = data[0];
                setDistrictId(d.SchoolDistrictId);
                setDistrictLogo(d.DistrictLogo || "");
            }
        } catch (err) {
            console.error("Error fetching district data:", err);
        } finally {
            setLoadingDistrict(false);
        }
    };

    /* ------------------- IMAGE HELPERS ------------------------ */
    const getImageUrl = (filename) =>
        filename?.startsWith("http")
            ? filename
            : `${MEDIA_BASE}/${MEDIA_FOLDER}/${filename}`;

    const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", MEDIA_FOLDER);
        const res = await axios.post(
            "https://media.swancreate.com/upload",
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        return res.data.fileName || res.data.filename || file.name;
    };

    /* ------------------- DISTRICT LOGO HANDLER ------------------- */
    const handleDistrictLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !districtId) return;
        try {
            const filename = await uploadImage(file);
            await axios.put(
                `https://figazuresql.swancreate.com/api/swa/SchoolDistrict/${districtId}`,
                { DistrictLogo: filename }
            );
            setDistrictLogo(filename);
        } catch (err) {
            console.error("Error uploading district logo:", err);
        }
    };

    /* --------------------- SCHOOL LOGO HANDLER ------------------- */
    const handleSchoolLogoChange = async (school) => {
        const file = school._uploadFile;
        if (!file) return;
        try {
            const filename = await uploadImage(file);
            await axios.put(
                `https://figazuresql.swancreate.com/api/swa/SchoolMst/${school.apiid}`,
                { LogoUrl: filename }
            );
            setSchools((prev) =>
                prev.map((s) =>
                    s.apiid === school.apiid ? { ...s, LogoUrl: filename } : s
                )
            );
        } catch (err) {
            console.error("Error uploading school logo:", err);
        }
    };

    /* ---------------------- EDIT HANDLERS ------------------------- */
    const handleEditClick = (school) => {
        setEditingSchoolId(school.apiid);
        setUpdatedData(school);
    };

    const handleSaveClick = async () => {
        const apiid = updatedData.apiid;
        // exclude auto‐calculated DailyEnrollment
        const { apiid: _, DailyEnrollment: __, ...payload } = updatedData;
        try {
            await axios.put(
                `https://figazuresql.swancreate.com/api/swa/SchoolMst/${apiid}`,
                payload
            );
            setSchools((prev) =>
                prev.map((s) => (s.apiid === apiid ? { ...s, ...payload } : s))
            );
            setEditingSchoolId(null);
        } catch (err) {
            console.error("Error saving school data:", err);
        }
    };

    const handleInputChange = (field, value) =>
        setUpdatedData((prev) => ({ ...prev, [field]: value }));

    /* ------------------- NEW SCHOOL HANDLERS ---------------------- */
    const handleAddSchool = async () => {
        try {
            await axios.post("https://figazuresql.swancreate.com/api/swa/SchoolMst", newSchool);
            setIsAddSchoolModalOpen(false);
            setNewSchool({
                SchoolName: "",
                SchoolCode: "",
                Address: "",
                City: "",
                State: "",
                Zip: "",
                Email: "",
                PhoneNumber: "",
                IsActive: true,
            });
            fetchSchools();
        } catch (err) {
            console.error("Error adding new school:", err);
        }
    };

    /* ----------------------- GRID COLUMNS ------------------------- */
    const columns = [
        {
            field: "Logo",
            headerName: "Logo",
            flex: 0.7,
            renderCell: (params) => {
                const school = params.row;
                return (
                    <Box display="flex" alignItems="center" gap={1}>
                        {school.LogoUrl ? (
                            <img
                                src={getImageUrl(school.LogoUrl)}
                                alt="Logo"
                                style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "contain",
                                    borderRadius: 4,
                                    border: `1px solid ${palette.border}`,
                                }}
                            />
                        ) : (
                            <Box
                                width={40}
                                height={40}
                                border={`1px dashed ${palette.border}`}
                                borderRadius={4}
                            />
                        )}
                        <Button
                            component="label"
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: palette.primaryBtn,
                                color: palette.text,
                                "&:hover": { backgroundColor: palette.primaryBtnHover },
                                minWidth: 0,
                                px: 1,
                                py: 0.5,
                                fontSize: "0.7rem",
                            }}
                        >
                            Upload
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                    school._uploadFile = e.target.files[0];
                                    handleSchoolLogoChange(school);
                                }}
                            />
                        </Button>
                    </Box>
                );
            },
        },
        {
            field: "SchoolName",
            headerName: "School Name",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.SchoolName || ""}
                        onChange={(e) => handleInputChange("SchoolName", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.SchoolName}</Typography>
                ),
        },
        {
            field: "SchoolCode",
            headerName: "School Code",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.SchoolCode || ""}
                        onChange={(e) => handleInputChange("SchoolCode", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.SchoolCode}</Typography>
                ),
        },
        {
            field: "Address",
            headerName: "Address",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.Address || ""}
                        onChange={(e) => handleInputChange("Address", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.Address}</Typography>
                ),
        },
        {
            field: "City",
            headerName: "City",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.City || ""}
                        onChange={(e) => handleInputChange("City", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.City}</Typography>
                ),
        },
        {
            field: "State",
            headerName: "State",
            flex: 0.5,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.State || ""}
                        onChange={(e) => handleInputChange("State", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.State}</Typography>
                ),
        },
        {
            field: "Zip",
            headerName: "Zip",
            flex: 0.5,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.Zip || ""}
                        onChange={(e) => handleInputChange("Zip", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.Zip}</Typography>
                ),
        },
        {
            field: "Email",
            headerName: "Email",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.Email || ""}
                        onChange={(e) => handleInputChange("Email", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.Email}</Typography>
                ),
        },
        {
            field: "PhoneNumber",
            headerName: "Phone Number",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.PhoneNumber || ""}
                        onChange={(e) => handleInputChange("PhoneNumber", e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.PhoneNumber}</Typography>
                ),
        },
        {
            field: "Enrollment",
            headerName: "Enrollment",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.Enrollment ?? ""}
                        onChange={(e) => handleInputChange("Enrollment", Number(e.target.value))}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        type="number"
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.Enrollment}</Typography>
                ),
        },
        {
            field: "AttendanceFactor",
            headerName: "Attendance Factor",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <TextField
                        value={updatedData.AttendanceFactor ?? ""}
                        onChange={(e) => handleInputChange("AttendanceFactor", Number(e.target.value))}
                        onKeyDown={(e) => e.stopPropagation()}
                        size="small"
                        fullWidth
                        type="number"
                        sx={{ ...textFieldSx }}
                    />
                ) : (
                    <Typography>{params.row.AttendanceFactor}</Typography>
                ),
        },
        {
            field: "IsActive",
            headerName: "Active",
            flex: 0.5,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <FormControl size="small" fullWidth sx={selectSx}>
                        <InputLabel>Active</InputLabel>
                        <Select
                            value={updatedData.IsActive ? "true" : "false"}
                            onChange={(e) => handleInputChange("IsActive", e.target.value === "true")}
                            label="Active"
                        >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </Select>
                    </FormControl>
                ) : (
                    <Typography color={params.row.IsActive ? palette.successBtn : palette.error}>
                        {params.row.IsActive ? "Yes" : "No"}
                    </Typography>
                ),
        },
        // ─────────────── NEW: isHighRate COLUMN ───────────────
        {
            field: "isHighRate",
            headerName: "High Rate?",
            flex: 0.5,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <FormControl size="small" fullWidth sx={selectSx}>
                        <InputLabel>High Rate?</InputLabel>
                        <Select
                            value={updatedData.isHighRate ? "true" : "false"}
                            onChange={(e) => handleInputChange("isHighRate", e.target.value === "true")}
                            label="High Rate?"
                        >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </Select>
                    </FormControl>
                ) : (
                    <Typography color={params.row.isHighRate ? palette.successBtn : palette.error}>
                        {params.row.isHighRate ? "Yes" : "No"}
                    </Typography>
                ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) =>
                editingSchoolId === params.row.apiid ? (
                    <Button
                        onClick={handleSaveClick}
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: palette.primaryBtn }}
                    >
                        Save
                    </Button>
                ) : (
                    <Button
                        onClick={() => handleEditClick(params.row)}
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: palette.secondaryBtn }}
                    >
                        Edit
                    </Button>
                ),
        },
    ];

    /* ----------------------------- RENDER -------------------------- */
    if (loadingSchools || loadingDistrict) {
        return (
            <Box mt={4} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            m={2}
            sx={{
                backgroundColor: palette.background,
                color: palette.text,
                minHeight: "100vh",
                p: 2,
            }}
        >
            {/* DISTRICT LOGO */}
            <Box display="flex" alignItems="center" gap={3} mb={4}>
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        bgcolor: palette.panel,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: `2px solid ${palette.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {districtLogo ? (
                        <img
                            src={getImageUrl(districtLogo)}
                            alt="District Logo"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                    ) : (
                        <Typography>No Logo</Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    component="label"
                    sx={{ backgroundColor: palette.primaryBtn }}
                >
                    Upload District Logo
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleDistrictLogoChange}
                    />
                </Button>
            </Box>

            {/* HEADER */}
            <Header title="SCHOOLS" subtitle="Manage and Edit School Locations" />

            {/* ADD NEW SCHOOL */}
            <Box mb={2} display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={() => setIsAddSchoolModalOpen(true)}
                    sx={{ backgroundColor: palette.successBtn }}
                >
                    Add New School
                </Button>
            </Box>

            {/* DATAGRID */}
            <Box mt={4} height="75vh">
                <DataGrid
                    rows={schools}
                    columns={columns}
                    getRowId={(row) => row.apiid}
                    disableSelectionOnClick
                    pageSize={10}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>

            {/* ADD SCHOOL MODAL */}
            <Modal open={isAddSchoolModalOpen} onClose={() => setIsAddSchoolModalOpen(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" mb={2} sx={{ color: palette.text }}>
                        Add New School
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {[
                            ["SchoolName", "School Name"],
                            ["SchoolCode", "School Code"],
                            ["Address", "Address"],
                            ["City", "City"],
                            ["State", "State"],
                            ["Zip", "Zip"],
                            ["Email", "Email"],
                            ["PhoneNumber", "Phone Number"],
                        ].map(([field, label]) => (
                            <TextField
                                key={field}
                                label={label}
                                value={newSchool[field]}
                                onChange={(e) =>
                                    setNewSchool((ns) => ({ ...ns, [field]: e.target.value }))
                                }
                                sx={textFieldSx}
                                variant="outlined"
                                size="small"
                            />
                        ))}

                        <FormControl fullWidth size="small" sx={selectSx}>
                            <InputLabel sx={{ color: palette.text }}>Active</InputLabel>
                            <Select
                                value={newSchool.IsActive ? "true" : "false"}
                                onChange={(e) =>
                                    setNewSchool((ns) => ({
                                        ...ns,
                                        IsActive: e.target.value === "true",
                                    }))
                                }
                                label="Active"
                            >
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </Select>
                        </FormControl>

                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button onClick={() => setIsAddSchoolModalOpen(false)} sx={{ color: palette.text }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAddSchool}
                                sx={{ backgroundColor: palette.successBtn }}
                            >
                                Add School
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
